import React from "react";
import classNames from "classnames";

import { useRetrieveZonesIndicators } from "@planzone/view-models";
import { Identifiable, Zone } from "@planzone/dto";
import { SelectList } from "@common/selectList";
import { ZoneAvatar } from "@common/zoneAvatar";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { ToggleArrow } from "./toggleArrow";
import { Props } from "./types";
import { Loader } from "./loader";
import styles from "./styles.scss";

function ZoneSelection({ placeholder, selectedZone, selectZone }: Props) {
  const filter = {
    projects: [],
    projectStatuses: [],
    projectsTags: [],
  };
  const {
    zones,
    index,
    canRetrieveNextZonesIndicators,
    isRequestPending,
    retrieveZonesIndicators,
  } = useRetrieveZonesIndicators(filter, "ascendingName", 10);
  const isZoneSelected = (zone: Identifiable<Zone>) =>
    selectedZone && selectedZone.uuid === zone.uuid;

  return (
    <SelectList
      rootClassName={styles.zoneSelection}
      listClassName={styles.listRoot}
    >
      {({ isOpen, select }) => ({
        input: (
          <div
            className={classNames(styles.input, { [styles.opened]: isOpen })}
          >
            {!!selectedZone ? (
              <div className={styles.selectedZone}>
                <div className={styles.folderIcon}>
                  <ZoneAvatar
                    name={selectedZone.name}
                    color={selectedZone.color}
                  />
                </div>
                <p className={styles.zone}>{selectedZone.name}</p>
              </div>
            ) : (
              <p className={styles.placeholder}>{placeholder}</p>
            )}
            <ToggleArrow className={styles.toggleArrow} />
          </div>
        ),
        list: (
          <div className={styles.list}>
            <InfiniteScroll canFetchOnIdle>
              {() => (
                <>
                  {[...zones]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((zone) => (
                      <div
                        key={zone.uuid}
                        className={classNames(styles.item, {
                          [styles.selected]: isZoneSelected(zone),
                        })}
                        onClick={() =>
                          select(selectZone(isZoneSelected(zone) ? null : zone))
                        }
                      >
                        <div className={styles.folderIcon}>
                          <ZoneAvatar name={zone.name} color={zone.color} />
                        </div>
                        <p className={styles.zone}>{zone.name}</p>
                      </div>
                    ))}
                  {isRequestPending && (
                    <>
                      <div>
                        <Loader />
                      </div>
                      <div>
                        <Loader />
                      </div>
                      <div>
                        <Loader />
                      </div>
                    </>
                  )}
                  <Flag
                    name="zones"
                    isDisabled={
                      isRequestPending || !canRetrieveNextZonesIndicators
                    }
                    retrieveItems={() =>
                      !isRequestPending &&
                      retrieveZonesIndicators({
                        filter,
                        index,
                        sort: "ascendingName",
                        size: 10,
                      })
                    }
                  />
                </>
              )}
            </InfiniteScroll>
          </div>
        ),
      })}
    </SelectList>
  );
}

export { ZoneSelection };
